/**
 * The Data Mapper's built-in transforms, for the Transform screen's single-value PREVIEW.
 *
 * shortcut: a COPY of the built-in half of packages/api/src/services/data-mapper/transform/
 * transformEngine.ts. The API's engine is the one an import uses; this copy only previews one
 * value in the browser. The images build per package, so the web cannot import across packages.
 * Keep the two in step when a transform's behaviour changes (transformPreview.test.ts pins the
 * behaviours the screen shows); promote both to a shared package if a third consumer appears.
 *
 * Two deliberate differences from the API copy:
 *   - a `custom` step is NEVER run here. User-authored code runs only in HQ's server sandbox
 *     (worker_threads, no environment, a hard timeout) at import time. The preview stops at the
 *     first enabled custom step and says so (`runs-at-import`); nothing after it is guessed at.
 *   - date-fns: the web pins date-fns 3, which has no `{ in: utc }` option. `format` is handed a
 *     UTCDate instance (utcDate.ts) so it writes UTC fields; `parse` lands in the system zone, so
 *     its fields are re-read as UTC (parseAsUtc); an ISO string without an offset is read as UTC
 *     by appending one. All three match what the API's `{ in: utc }` calls do.
 */
import { format, parse, parseISO, isValid } from "date-fns";

import type {
  Transform,
  TransformConfig,
  TransformContext,
  TransformType,
  DateFormatConfig,
  ValueLookupConfig,
  ConcatConfig,
  SplitConfig,
  TrimConfig,
  TypeCoercionConfig,
  RegexConfig,
  TemplateConfig,
  DefaultConfig,
  SourceDataRow,
} from "./types";
import { UTCDate } from "./utcDate";

/** One step of a preview. */
export interface PreviewStep {
  /** The transform's position in the chain as run (enabled steps, by order). */
  index: number;
  transformId: string;
  type: TransformType;
  /** `runs-at-import`: a custom step, which the browser never executes. */
  status: "ok" | "error" | "runs-at-import";
  output?: unknown;
  error?: string;
}

export interface PreviewResult {
  /** `done`: every step ran. `error`: a step failed. `runs-at-import`: stopped at a custom step. */
  status: "done" | "error" | "runs-at-import";
  /** The final value when done; the last value computed otherwise. */
  value: unknown;
  steps: PreviewStep[];
}

/**
 * Preview a transform chain on ONE value: the enabled transforms, in `order`, each fed the
 * previous output. Stops at the first error, and at the first custom step (which runs only in
 * HQ's server sandbox at import time).
 */
export function previewTransformChain(
  value: unknown,
  transforms: readonly Pick<Transform, "id" | "type" | "config" | "order" | "enabled">[],
  sourceRow: SourceDataRow = {},
  context: TransformContext = {}
): PreviewResult {
  const run = [...transforms].filter((t) => t.enabled).sort((a, b) => a.order - b.order);
  const steps: PreviewStep[] = [];
  let current = value;
  for (let index = 0; index < run.length; index++) {
    const t = run[index];
    if (t.type === "custom" || t.config?.type === "custom") {
      steps.push({ index, transformId: t.id, type: t.type, status: "runs-at-import" });
      return { status: "runs-at-import", value: current, steps };
    }
    try {
      current = executeBuiltinTransform(current, t.config, sourceRow, context);
      steps.push({ index, transformId: t.id, type: t.type, status: "ok", output: current });
    } catch (error) {
      const message = errorMessage(error, "Transform failed");
      steps.push({ index, transformId: t.id, type: t.type, status: "error", error: message });
      return { status: "error", value: current, steps };
    }
  }
  return { status: "done", value: current, steps };
}

/** Execute one non-custom transform synchronously. A custom config throws (never evaluated here). */
export function executeBuiltinTransform(
  value: unknown,
  config: TransformConfig,
  sourceRow: SourceDataRow,
  context: TransformContext = {}
): unknown {
  switch (config.type) {
    case "dateFormat":
      return transformDateFormat(value, config as DateFormatConfig);
    case "valueLookup":
      return transformValueLookup(value, config as ValueLookupConfig, context);
    case "concat":
      return transformConcat(value, config as ConcatConfig, sourceRow);
    case "split":
      return transformSplit(value, config as SplitConfig);
    case "trim":
      return transformTrim(value, config as TrimConfig);
    case "uppercase":
      return value === null || value === undefined ? null : String(value).toUpperCase();
    case "lowercase":
      return value === null || value === undefined ? null : String(value).toLowerCase();
    case "capitalize": {
      if (value === null || value === undefined) return null;
      const str = String(value);
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }
    case "typeCoercion":
      return transformTypeCoercion(value, config as TypeCoercionConfig);
    case "regex":
      return transformRegex(value, config as RegexConfig);
    case "template":
      return transformTemplate(value, config as TemplateConfig, sourceRow);
    case "default":
      return transformDefault(value, config as DefaultConfig);
    case "custom":
      throw new Error("Custom code runs in HQ's server sandbox at import time, not in the browser.");
    default:
      return value;
  }
}

/** An ISO string with no offset is UTC wall-clock time (the API's `parseISO(value, { in: utc })`). */
function parseIsoAsUtc(value: string): Date {
  const text = value.trim();
  const m = /^([^T ]+)(?:[T ](.+))?$/.exec(text);
  if (!m) return parseISO(text);
  if (m[2] === undefined) return parseISO(`${m[1]}T00:00:00Z`);
  if (/(?:[zZ]|[+-]\d{2}(?::?\d{2})?)$/.test(m[2])) return parseISO(text);
  return parseISO(`${m[1]}T${m[2]}Z`);
}

/**
 * `parse` with the input read as UTC wall-clock time. date-fns 3's parse always lands in the
 * SYSTEM time zone (its DateToSystemTimezoneSetter), so the parsed local fields are re-read as
 * UTC, unless the format itself carries an offset or a timestamp (X, x, O, t, T), whose instant
 * is already absolute.
 *
 * shortcut: a wall-clock time that falls in the browser's own DST gap (e.g. 02:30 on a
 * spring-forward night) is normalised by the browser before it is re-read, so the preview shows it
 * an hour later than the import will. Preview-only; date-fns 4 (`{ in: utc }`) removes it.
 */
function parseAsUtc(value: string, inputFormat: string): Date {
  const local = parse(value, inputFormat, new Date());
  if (!isValid(local)) return local;
  const tokens = inputFormat.replace(/'[^']*'/g, "");
  if (/[XxOtT]/.test(tokens)) return local;
  return new Date(
    Date.UTC(
      local.getFullYear(),
      local.getMonth(),
      local.getDate(),
      local.getHours(),
      local.getMinutes(),
      local.getSeconds(),
      local.getMilliseconds()
    )
  );
}

/** Parse a date-like value; wall-clock inputs without an offset are read as UTC. */
function toUtcDate(value: unknown, inputFormat?: string): Date {
  let date: Date;
  if (value instanceof Date) {
    date = new Date(value.getTime());
  } else if (typeof value === "string") {
    date = new Date(NaN);
    if (inputFormat) {
      try {
        date = parseAsUtc(value, inputFormat);
      } catch {
        date = new Date(NaN);
      }
    }
    if (!isValid(date)) date = parseIsoAsUtc(value);
  } else if (typeof value === "number") {
    date = new Date(value);
  } else {
    throw new Error(`Cannot convert ${typeof value} to date`);
  }
  if (!isValid(date)) throw new Error(`Invalid date value: ${String(value)}`);
  return new Date(date.getTime());
}

function transformDateFormat(value: unknown, config: DateFormatConfig): string | null {
  if (value === null || value === undefined || value === "") return null;
  const date = toUtcDate(value, config.inputFormat);
  // A UTCDate instance: date-fns 3 keeps its constructor, so the fields it reads are UTC.
  return format(new UTCDate(date.getTime()), config.outputFormat);
}

function lookupSource(config: ValueLookupConfig, context: TransformContext): Record<string, unknown> {
  const tables = context.lookupTables;
  if (config.lookupTableId && tables) {
    if (!Object.prototype.hasOwnProperty.call(tables, config.lookupTableId)) {
      throw new Error(`Lookup table ${config.lookupTableId} was not provided`);
    }
    const map: Record<string, unknown> = {};
    for (const entry of tables[config.lookupTableId]) {
      Object.defineProperty(map, String(entry.sourceValue), {
        value: entry.targetValue,
        enumerable: true,
        writable: true,
        configurable: true,
      });
    }
    return map;
  }
  return config.lookupMap ?? {};
}

function transformValueLookup(value: unknown, config: ValueLookupConfig, context: TransformContext): unknown {
  if (value === null || value === undefined) return config.defaultValue;
  const sourceMap = lookupSource(config, context);
  const lookupKey = config.caseSensitive ? String(value) : String(value).toLowerCase();
  const lookupMap: Record<string, unknown> = config.caseSensitive
    ? sourceMap
    : Object.fromEntries(Object.entries(sourceMap).map(([k, v]) => [k.toLowerCase(), v]));
  if (Object.prototype.hasOwnProperty.call(lookupMap, lookupKey)) return lookupMap[lookupKey];
  return config.defaultValue !== undefined ? config.defaultValue : value;
}

function transformConcat(value: unknown, config: ConcatConfig, sourceRow: SourceDataRow): string {
  const fields = config.fields && config.fields.length > 0 ? config.fields : ["__value__"];
  const values = fields.map((field) => (field === "__value__" ? (value ?? "") : (sourceRow[field] ?? "")));
  return values.map(String).join(config.separator ?? "");
}

function transformSplit(value: unknown, config: SplitConfig): string | null {
  if (value === null || value === undefined) return null;
  const parts = String(value).split(config.separator);
  const index = config.index < 0 ? parts.length + config.index : config.index;
  return parts[index] ?? null;
}

function transformTrim(value: unknown, config: TrimConfig): string | null {
  if (value === null || value === undefined) return null;
  const str = String(value);
  if (config.chars) {
    const charClass = `[${escapeCharClass(config.chars)}]`;
    switch (config.trimType) {
      case "start":
        return str.replace(new RegExp(`^${charClass}+`, "u"), "");
      case "end":
        return str.replace(new RegExp(`${charClass}+$`, "u"), "");
      default:
        return str.replace(new RegExp(`^${charClass}+|${charClass}+$`, "gu"), "");
    }
  }
  switch (config.trimType) {
    case "start":
      return str.trimStart();
    case "end":
      return str.trimEnd();
    default:
      return str.trim();
  }
}

const DECIMAL_NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;
const DIGITS = "0123456789abcdefghijklmnopqrstuvwxyz";

function parseStrictInteger(raw: unknown, radix: number): number {
  if (typeof raw === "number") {
    if (!Number.isSafeInteger(raw)) throw new Error(`Cannot convert "${raw}" to integer`);
    return raw;
  }
  const str = String(raw).trim();
  if (radix === 10 && /^[+-]?\d+(?:\.0*)?$/.test(str)) {
    const int = Number(str);
    if (!Number.isSafeInteger(int)) throw new Error(`Cannot convert "${raw}" to integer`);
    return Object.is(int, -0) ? 0 : int;
  }
  const allowed = DIGITS.slice(0, radix);
  const body = str.replace(/^[+-]/, "").toLowerCase();
  if (body.length === 0 || [...body].some((ch) => !allowed.includes(ch))) {
    throw new Error(`Cannot convert "${raw}" to integer`);
  }
  const int = parseInt(str, radix);
  if (!Number.isSafeInteger(int)) throw new Error(`Cannot convert "${raw}" to integer`);
  return int;
}

function transformTypeCoercion(value: unknown, config: TypeCoercionConfig): unknown {
  if (value === null || value === undefined) return null;
  const isBlankString = typeof value === "string" && value.trim() === "";
  switch (config.targetType) {
    case "string":
      return String(value);
    case "number": {
      if (isBlankString) return null;
      let num: number;
      if (typeof value === "number") num = value;
      else if (typeof value === "string") num = DECIMAL_NUMBER.test(value.trim()) ? Number(value.trim()) : NaN;
      else if (typeof value === "boolean") num = value ? 1 : 0;
      else num = NaN;
      if (!Number.isFinite(num)) throw new Error(`Cannot convert "${String(value)}" to number`);
      if (config.options?.decimalPlaces !== undefined) return Number(num.toFixed(config.options.decimalPlaces));
      return num;
    }
    case "integer": {
      if (isBlankString) return null;
      const radix = config.options?.radix || 10;
      if (typeof value === "boolean") return value ? 1 : 0;
      if (typeof value !== "number" && typeof value !== "string") {
        throw new Error(`Cannot convert "${String(value)}" to integer`);
      }
      return parseStrictInteger(value, radix);
    }
    case "boolean": {
      if (typeof value === "boolean") return value;
      if (typeof value === "number") return value !== 0;
      if (isBlankString) return null;
      const str = String(value).trim().toLowerCase();
      if (["true", "1", "yes", "y"].includes(str)) return true;
      if (["false", "0", "no", "n"].includes(str)) return false;
      throw new Error(`Cannot convert "${String(value)}" to boolean`);
    }
    case "date":
      if (isBlankString) return null;
      return toUtcDate(value, config.options?.dateFormat);
    default:
      return value;
  }
}

function transformRegex(value: unknown, config: RegexConfig): string | null {
  if (value === null || value === undefined) return null;
  // The person's own pattern on their own sample value, as in the source app's live preview.
  const regex = new RegExp(config.pattern, config.flags || "");
  return String(value).replace(regex, config.replacement);
}

/** Placeholder names referenced as `{name}` in a template. */
export function extractTemplateFields(template: string): string[] {
  const names = new Set<string>();
  for (const match of template.matchAll(/(?<!\$)\{([^{}\s]+)\}/g)) names.add(match[1]);
  return [...names];
}

function transformTemplate(value: unknown, config: TemplateConfig, sourceRow: SourceDataRow): string {
  const listed = new Set(config.fields ?? []);
  const lookup = (field: string, valueAlias: string) =>
    String((field === valueAlias ? value : sourceRow[field]) ?? "");
  return (config.template ?? "").replace(
    /\$\{(\w+)\}|\{([^{}]+)\}/g,
    (match, dollarField: string | undefined, braceField: string | undefined) => {
      if (dollarField !== undefined) return lookup(dollarField, "value");
      if (braceField !== undefined && listed.has(braceField)) return lookup(braceField, "__value__");
      return match;
    }
  );
}

function transformDefault(value: unknown, config: DefaultConfig): unknown {
  switch (config.condition) {
    case "null":
      return value === null ? config.value : value;
    case "undefined":
      return value === undefined ? config.value : value;
    case "empty":
      return value === null || value === undefined || value === "" ? config.value : value;
    case "always":
      return config.value;
    default:
      return value;
  }
}

function escapeCharClass(str: string): string {
  return str.replace(/[\\\]^[-]/g, "\\$&");
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}
